import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiInformationCircle, HiChevronDown, HiTrash } from 'react-icons/hi';
import Toast from '../../components/toaster';
import {
  getDashboardFestivalBanner,
  upsertDashboardFestivalBanner,
  deleteDashboardFestivalBanner,
} from '../../services/dashboardFestivalBannerService';
import { fetchModuleList } from '../../services/moduleService';
import { uploadImage } from '../../services/assetService';

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="fb-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="40" stroke="url(#fb-gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" transform="rotate(-90 50 50)" />
      </svg>
    </div>
  </div>
);

const emptyForm = () => ({
  module_id: '',
  image: '',
  is_enable: false,
});

const getModuleId = (m) => String(m?._id || m?.module_id || m?.id || '');

const MODULE_PAGE_SIZE = 20;

const parseModuleListResponse = (response) => {
  let list = [];
  if (Array.isArray(response?.data?.data)) list = response.data.data;
  else if (Array.isArray(response?.data?.list)) list = response.data.list;
  else if (Array.isArray(response?.data)) list = response.data;
  else if (Array.isArray(response)) list = response;

  const total =
    response?.total ??
    response?.totalCount ??
    response?.data?.total ??
    response?.data?.totalCount ??
    list.length;

  const totalPages =
    response?.totalPages ??
    response?.data?.totalPages ??
    Math.max(1, Math.ceil(Number(total) / MODULE_PAGE_SIZE) || 1);

  return { list, total: Number(total) || list.length, totalPages: Number(totalPages) || 1 };
};

const DashboardFestivalBanner = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [bannerId, setBannerId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [loadError, setLoadError] = useState(null);

  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [modulesLoadingMore, setModulesLoadingMore] = useState(false);
  const [modulePage, setModulePage] = useState(1);
  const [moduleTotalPages, setModuleTotalPages] = useState(1);
  const [moduleDropdownOpen, setModuleDropdownOpen] = useState(false);

  const [imageSource, setImageSource] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [imageUploadedUrl, setImageUploadedUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [originalImage, setOriginalImage] = useState('');

  const isLoadingRef = useRef(false);
  const moduleDropdownRef = useRef(null);
  const modulesLoadingMoreRef = useRef(false);

  const applyBanner = useCallback((data) => {
    if (!data) {
      setBannerId(null);
      setFormData(emptyForm());
      setOriginalImage('');
      return;
    }
    const image = data.image || '';
    const moduleId = data.module_id
      ? String(typeof data.module_id === 'object' ? data.module_id._id || data.module_id : data.module_id)
      : '';
    setBannerId(data._id || null);
    setFormData({
      module_id: moduleId,
      image,
      is_enable: Boolean(data.is_enable),
    });
    setOriginalImage(image);
  }, []);

  const loadBanner = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getDashboardFestivalBanner();
      applyBanner(res?.data ?? null);
    } catch (err) {
      setLoadError(err.message || 'Failed to load festival banner');
      applyBanner(null);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [applyBanner]);

  const loadModules = useCallback(async (page = 1, append = false) => {
    if (append) {
      if (modulesLoadingMoreRef.current) return;
      modulesLoadingMoreRef.current = true;
      setModulesLoadingMore(true);
    } else {
      setModulesLoading(true);
    }

    try {
      const response = await fetchModuleList(page, MODULE_PAGE_SIZE, '', {});
      const { list, totalPages } = parseModuleListResponse(response);

      setModulePage(page);
      setModuleTotalPages(totalPages);
      setModules((prev) => {
        if (!append) return list;
        const seen = new Set(prev.map(getModuleId));
        const next = list.filter((m) => {
          const id = getModuleId(m);
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        return [...prev, ...next];
      });
    } catch {
      if (!append) {
        setModules([]);
        setModulePage(1);
        setModuleTotalPages(1);
        Toast.error('Failed to load modules');
      }
    } finally {
      if (append) {
        modulesLoadingMoreRef.current = false;
        setModulesLoadingMore(false);
      } else {
        setModulesLoading(false);
      }
    }
  }, []);

  const loadMoreModules = useCallback(() => {
    if (modulesLoading || modulesLoadingMoreRef.current) return;
    if (modulePage >= moduleTotalPages) return;
    loadModules(modulePage + 1, true);
  }, [loadModules, modulePage, moduleTotalPages, modulesLoading]);

  const handleModuleListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (nearBottom) loadMoreModules();
    },
    [loadMoreModules]
  );

  useEffect(() => {
    loadBanner();
    loadModules(1, false);
  }, [loadBanner, loadModules]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moduleDropdownRef.current && !moduleDropdownRef.current.contains(event.target)) {
        setModuleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetImageState = () => {
    setImageSource('url');
    setImageFile(null);
    setImageUploadedUrl('');
    setUploadingImage(false);
    setImageUploadError('');
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    setImageUploadError('');
    if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
    try {
      const uploadedUrl = await uploadImage(file);
      setImageFile(file);
      setImageUploadedUrl(uploadedUrl);
      setFormData((prev) => ({ ...prev, image: uploadedUrl }));
      setOriginalImage(uploadedUrl);
    } catch (err) {
      setImageUploadError(err.message || 'Failed to upload image. Please try again.');
      setImageFile(null);
      setImageUploadedUrl('');
      setFormData((prev) => ({ ...prev, image: '' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.module_id) errs.module_id = 'Module is required';
    if (!formData.image.trim()) errs.image = 'Image is required';
    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    setSaving(true);
    setFormErrors({});
    setFormGeneralError('');
    try {
      const res = await upsertDashboardFestivalBanner({
        module_id: formData.module_id,
        image: formData.image.trim(),
        is_enable: Boolean(formData.is_enable),
      });
      applyBanner(res?.data ?? null);
      resetImageState();
      Toast.success(bannerId ? 'Festival banner updated successfully' : 'Festival banner saved successfully');
    } catch (err) {
      setFormGeneralError(err.message || 'Failed to save festival banner');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!bannerId) return;
    setDeleting(true);
    try {
      await deleteDashboardFestivalBanner(bannerId);
      applyBanner(null);
      resetImageState();
      setShowDeleteModal(false);
      Toast.success('Festival banner deleted successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to delete festival banner');
    } finally {
      setDeleting(false);
    }
  };

  const selectedModule = modules.find((m) => getModuleId(m) === formData.module_id);
  const moduleLabel = selectedModule
    ? `${selectedModule.title || formData.module_id}${selectedModule.is_active === false ? ' (inactive)' : ''}`
    : formData.module_id || '';

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto px-4 py-4 md:px-8 md:py-6">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">Festival Banner</h1>
            </div>
          </div>

          {loading ? (
            <FullPageSpinner />
          ) : loadError ? (
            <div className="bg-white rounded-2xl p-6 shadow-md border max-w-xl">
              <p className="text-red-600 text-sm mb-4">{loadError}</p>
              <button
                onClick={loadBanner}
                className="h-9 px-6 text-sm font-bold text-white rounded-lg bg-brand hover:opacity-90"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-6 shadow-md border max-w-xl w-full">
              {/* Header */}
              <div className="mb-2">
                <h3 className="text-xl font-bold text-black">
                  {bannerId ? 'Current Banner' : 'Festival Banner'}
                </h3>
              </div>

              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Set the promotional banner shown on the app home dashboard. Link a module and image;
                toggle visibility when ready to show.
                {bannerId && (
                  <>
                    {' '}
                    Currently{' '}
                    <span className="font-normal text-gray-500">
                      {formData.is_enable ? 'visible' : 'hidden'}
                    </span>{' '}
                    on the app.
                  </>
                )}
              </p>

              <div className="space-y-4">
                {formGeneralError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {formGeneralError}
                  </div>
                )}

                {/* Module */}
                <div>
                  <label className="block text-sm font-bold text-gray-500 mb-1.5">
                    Module <span className="text-red-500">*</span>
                  </label>
                  <div className="relative custom-dropdown-container" ref={moduleDropdownRef}>
                    <button
                      type="button"
                      disabled={modulesLoading}
                      onClick={() => setModuleDropdownOpen((open) => !open)}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none flex items-center justify-between text-left bg-white ${
                        formErrors.module_id ? 'border-red-500' : 'border-gray-300'
                      } ${modulesLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <span className={`truncate ${formData.module_id ? 'text-black font-normal' : 'text-gray-400'}`}>
                        {modulesLoading
                          ? 'Loading modules...'
                          : formData.module_id
                            ? moduleLabel
                            : 'Select a module'}
                      </span>
                      <HiChevronDown
                        className={`w-4 h-4 flex-shrink-0 text-gray-500 transition-transform ${
                          moduleDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {moduleDropdownOpen && !modulesLoading && (
                      <div
                        onScroll={handleModuleListScroll}
                        className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl py-1 max-h-60 overflow-y-auto"
                      >
                        {modules.length === 0 &&
                        !(formData.module_id && !modules.some((m) => getModuleId(m) === formData.module_id)) ? (
                          <div className="px-4 py-2 text-sm text-gray-500">No modules found</div>
                        ) : (
                          <>
                            {formData.module_id &&
                              !modules.some((m) => getModuleId(m) === formData.module_id) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setModuleDropdownOpen(false);
                                  }}
                                  className="w-full px-4 py-2 text-sm text-left bg-brand/10 text-brand font-semibold"
                                >
                                  {moduleLabel}
                                </button>
                              )}
                            {modules.map((m) => {
                              const id = getModuleId(m);
                              if (!id) return null;
                              const inactive = m.is_active === false;
                              const isSelected = formData.module_id === id;
                              const label = `${m.title || id}${inactive ? ' (inactive)' : ''}`;
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, module_id: id });
                                    if (formErrors.module_id) {
                                      setFormErrors({ ...formErrors, module_id: '' });
                                    }
                                    setModuleDropdownOpen(false);
                                  }}
                                  className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 ${
                                    isSelected ? 'bg-brand/10 text-brand font-semibold' : 'text-gray-700'
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                            {modulesLoadingMore && (
                              <div className="px-4 py-2 flex items-center justify-center gap-2 text-xs text-gray-500">
                                <svg
                                  className="animate-spin h-3.5 w-3.5 text-brand"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                Loading more...
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {formErrors.module_id && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.module_id}</p>
                  )}
                </div>

                {/* Image */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-bold text-gray-500">
                      Banner image <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 group relative">
                        <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                        <span
                          className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                            imageSource === 'url'
                              ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                              : 'w-[175px] sm:w-fit sm:max-w-[240px]'
                          }`}
                        >
                          {imageSource === 'url'
                            ? 'Switch left to choose a file from your device'
                            : 'Switch right to paste an image URL.'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const next = imageSource === 'url' ? 'file' : 'url';
                          setImageSource(next);
                          setImageFile(null);
                          setImageUploadedUrl('');
                          setImageUploadError('');
                          if (formErrors.image) setFormErrors((prev) => ({ ...prev, image: '' }));
                          setFormData((prev) => ({ ...prev, image: originalImage }));
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                          imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                            imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {imageSource === 'url' ? (
                    <div>
                      <input
                        type="url"
                        value={formData.image}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, image: val });
                          setOriginalImage(val);
                          if (formErrors.image) setFormErrors({ ...formErrors, image: '' });
                        }}
                        placeholder="https://cdn.vedify.app/banners/..."
                        className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:border-gray-500 ${
                          formErrors.image ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {formData.image && (
                        <div className="mt-2">
                          <img
                            src={formData.image}
                            alt="Banner preview"
                            className="w-full h-36 object-cover rounded-xl bg-gray-100"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      {formErrors.image && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.image}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleImageUpload(file);
                        }}
                        disabled={uploadingImage}
                        className={`w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:border-gray-500 ${
                          imageUploadError || formErrors.image ? 'border-red-500' : 'border-gray-300'
                        } ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      {uploadingImage && (
                        <div className="mt-2 flex items-center gap-2">
                          <svg
                            className="animate-spin h-4 w-4 text-brand"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          <span className="text-xs text-gray-600">Uploading image...</span>
                        </div>
                      )}
                      {(imageUploadedUrl || imageFile || formData.image) && !uploadingImage && (
                        <div className="mt-2">
                          <img
                            src={
                              imageUploadedUrl ||
                              (imageFile ? URL.createObjectURL(imageFile) : formData.image)
                            }
                            alt="Banner preview"
                            className="w-full h-36 object-cover rounded-xl bg-gray-100"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      {imageUploadError && (
                        <p className="mt-1 text-xs text-red-500">{imageUploadError}</p>
                      )}
                      {formErrors.image && !imageUploadError && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.image}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Enable */}
                <div className="flex items-center justify-between pt-1">
                  <div>
                    <label className="text-sm font-bold text-gray-500">Show on app dashboard</label>
                    <p className="text-xs text-gray-400 mt-0.5">
                      When off, the app hides the banner.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_enable: !formData.is_enable })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.is_enable ? 'bg-brand' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.is_enable ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Actions — bottom right; Delete only when banner exists */}
                <div className="flex items-center justify-end gap-2 pt-2">
                  {bannerId && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(true)}
                      disabled={saving || deleting}
                      className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center disabled:opacity-50"
                      title="Delete banner"
                    >
                      <HiTrash className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || uploadingImage || modulesLoading}
                    className="h-8 px-5 text-sm font-bold text-white rounded-lg bg-brand hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Spinner />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && bannerId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete Festival Banner</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Delete this festival banner? The app dashboard will stop showing it. This cannot be
                undone.
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Spinner />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardFestivalBanner;
